import { useMutation, useQueryClient } from '@tanstack/react-query';
import { generateTagMemberQueryOptions } from '@/hooks/tags/use-tag-members';
import { tagListQueryOptions } from '@/hooks/tags/use-tags';
import type { RespondedType, ResponseError } from '@/models/response';
import type { Tag } from '@/models/user/tag';
import { fetcher } from '@/utils/fetcher';

type Adjustment = Array<Pick<Tag, 'Id'> & { adjustment: 'add' | 'remove' }>;

async function adjustTagsByFriend(friendId: number, adjustments: Adjustment) {
  return Promise.allSettled([
    fetcher(
      '/AddFriendToTag',
      {
        FriendId: friendId,
        TagIds: adjustments.filter(({ adjustment }) => adjustment === 'add').map(({ Id }) => Id),
      },
      { validResponseCodes: [0] },
    ),
    fetcher(
      '/RemoveFriendFromTag',
      {
        FriendId: friendId,
        TagIds: adjustments.filter(({ adjustment }) => adjustment === 'remove').map(({ Id }) => Id),
      },
      { validResponseCodes: [0] },
    ),
  ]).then(() => ({
    Code: 0,
    Msg: 'success',
  }));
}

export default function useAdjustTagsByFriend(friendId: number) {
  const queryClient = useQueryClient();

  return useMutation<RespondedType, ResponseError, Adjustment>({
    mutationKey: ['/AdjustFriendTags'],
    mutationFn: (adjustments) => adjustTagsByFriend(friendId, adjustments),
    onSuccess: (_, params) =>
      Promise.allSettled([
        queryClient.invalidateQueries(tagListQueryOptions),
        ...params.map(({ Id }) => queryClient.invalidateQueries(generateTagMemberQueryOptions(Id))),
      ]),
  });
}
